import '../styles/styles.scss';
import '../styles/tailwind.css';

import { ReactNode } from 'react';
import { AppProps } from 'next/app.js';
import getConfig from 'next/config';
import { LoadingSpinner } from '@havenengineering/module-shared-library/dist/components/LoadingSpinner/LoadingSpinner';

import { Header } from '../components/Header/Header';
import { ExperimentProvider, useExperiments } from '../helpers/experiments';
import { useInitializeGTM } from '../helpers/useInitializeGTM';

interface PublicConfig {
  AMPLITUDE_API_KEY: string;
}

const getPublicConfig = (): PublicConfig | undefined => {
  const config = getConfig() as { publicRuntimeConfig?: PublicConfig } | undefined;
  return config?.publicRuntimeConfig;
};

const hasExperimentOverridesCookie = (): boolean =>
  typeof document !== 'undefined' && document.cookie.includes('experiment_overrides=');

const FlagsLoadingGate = ({ children }: { children: ReactNode }) => {
  const { isLoading } = useExperiments();

  if (isLoading && !hasExperimentOverridesCookie()) {
    return (
      <article data-testid="loading-spinner">
        <LoadingSpinner loading={true} />
      </article>
    );
  }

  return <>{children}</>;
};

export const IdentityApp = ({ Component, pageProps, router }: AppProps) => {
  useInitializeGTM();

  const clientId = (pageProps as { clientContext?: { id?: string } })?.clientContext?.id;
  const publicConfig = getPublicConfig();
  const isErrorPage =
    router.pathname === '/_error' || router.pathname === '/404' || router.pathname === '/500';

  const tree = (
    <>
      <Header clientId={clientId} />
      <Component {...pageProps} />
    </>
  );

  if (isErrorPage) return tree;

  // ExperimentProvider always wraps the tree so client-side cookie overrides
  // (`experiment_overrides`) work even when no API key is configured (e.g. in
  // the E2E environment). FlagsLoadingGate blocks rendering until variants
  // resolve so flag consumers don't mount with stale defaults; the cookie
  // override bypasses the gate to keep E2E fast.
  return (
    <ExperimentProvider apiKey={publicConfig?.AMPLITUDE_API_KEY ?? ''}>
      <FlagsLoadingGate>{tree}</FlagsLoadingGate>
    </ExperimentProvider>
  );
};

export default IdentityApp;
